import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { bootstrapMobilePush } from "../lib/mobilePush";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = localStorage.getItem("kb_token");
    if (!token) {
      setUser(null);
      setSeller(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setSeller(data.seller);
      // Kick off Capacitor push registration (no-op on web).
      // Fire-and-forget — we never want this to block the auth flow.
      bootstrapMobilePush().catch(() => {});
    } catch {
      localStorage.removeItem("kb_token");
      setUser(null);
      setSeller(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (phone, code, name) => {
    // Pick up referral code from localStorage if the visitor landed
    // via a `?ref=CODE` URL (see App.js referral-capture effect). The
    // code is applied server-side only on brand-new user creation,
    // then cleared here so it never leaks into a subsequent login.
    let referral_code = null;
    try {
      referral_code = localStorage.getItem("kb_referral_code") || null;
    } catch { /* SSR / private mode */ }
    const { data } = await api.post("/auth/otp/verify", { phone, code, name, referral_code });
    localStorage.setItem("kb_token", data.token);
    if (referral_code) {
      try { localStorage.removeItem("kb_referral_code"); } catch {}
    }
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("kb_token");
    // Clear the pinned tenant slug — set by super-admin impersonation
    // or dev override. Without this, signing back in as a super-admin
    // keeps every subsequent API call scoped to the impersonated tenant.
    localStorage.removeItem("kb_tenant");
    setUser(null);
    setSeller(null);
  };

  return (
    <AuthContext.Provider value={{ user, seller, loading, login, logout, refresh, setSeller }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
